import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaExchangeAlt,
  FaExternalLinkAlt,
  FaLayerGroup,
} from "react-icons/fa";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./Swap.scss";
import Header from "../Header";
import Footer from "../Footer/Footer";
import TelegramPopup from "../../Common/TelegramPopup/TelegramPopup";
import {
  RADIX_BUY_URL,
  RADIX_CHARTS_URL,
  SWAP_WIDGET_URL,
} from "../../../config/siteContent";
import {
  fetchDckslapActivitySnapshot,
  getCachedDckslapActivitySnapshot,
} from "../../../dckslap/services/activityStats";
import {
  CHART_RANGE_OPTIONS,
  fetchSwapExplorerChartSeries,
} from "./swapChartData";
import {
  DEFAULT_SWAP_BASE_RESOURCE,
  DEFAULT_SWAP_QUOTE_RESOURCE,
  getSwapWidgetSyncConfig,
  getSwapExplorerAssetByAddress,
  getSwapExplorerChartUrl,
  getSwapExplorerFeaturedPairs,
  getSwapExplorerQuoteCandidates,
  isSwapExplorerPairChartSupported,
  SWAP_EXPLORER_ASSETS,
} from "./swapExplorerAssets";
import {
  fetchSwapExplorerPulse,
  getCachedSwapExplorerPulse,
} from "./swapIntel";

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const WIDGET_IFRAME_NAME = "swap-widget-frame";
const WIDGET_SYNC_AMOUNT = "100";

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const oneDecimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

const formatCompactNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? compactFormatter.format(numericValue) : "0";
};

const formatInteger = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? integerFormatter.format(numericValue) : "0";
};

const formatRatio = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${oneDecimalFormatter.format(numericValue)}x` : "0x";
};

const formatFee = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${oneDecimalFormatter.format(numericValue)} XRD` : "n/a";
};

const formatTimeAgo = (value) => {
  const timestamp = Date.parse(value || "");
  if (!Number.isFinite(timestamp)) {
    return "just now";
  }

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));

  if (diffMinutes < 1) {
    return "just now";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.round(diffHours / 24);
  return `${diffDays}d ago`;
};

const formatChartValue = (value, decimalPlaces = 5) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: Math.min(Math.max(decimalPlaces, 2), 6),
  });
};

const formatChartTick = (timestamp, rangeId) => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  if (rangeId === "1D") {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (rangeId === "1W") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const getChartScale = (points) => {
  if (!points.length) {
    return {
      chartHigh: null,
      chartLow: null,
      yAxisDomain: ["auto", "auto"],
    };
  }

  const chartHigh = Math.max(...points.map((point) => point.high));
  const chartLow = Math.min(...points.map((point) => point.low));
  const chartRange = chartHigh - chartLow;
  const paddingAnchor = Math.max(Math.abs(chartHigh), Math.abs(chartLow), 1);
  const yAxisPadding = chartRange > 0 ? chartRange * 0.12 : paddingAnchor * 0.02;
  const yAxisDomain = [chartLow - yAxisPadding, chartHigh + yAxisPadding];

  return {
    chartHigh,
    chartLow,
    yAxisDomain,
  };
};

const SwapChartTooltip = ({ active, payload, label, decimalPlaces }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  return (
    <div className="swap-chart-tooltip">
      <span>{new Date(label).toLocaleString()}</span>
      <strong>{formatChartValue(point.close, decimalPlaces)}</strong>
      <small>
        H {formatChartValue(point.high, decimalPlaces)} / L {formatChartValue(point.low, decimalPlaces)}
      </small>
    </div>
  );
};

const CandleBodyShape = ({ x, y, width, height, payload }) => {
  const candleWidth = Math.max(4, Math.min(width - 2, 12));
  const bodyHeight = Math.max(height, 2);
  const bodyY = height >= 2 ? y : y - (2 - height) / 2;
  const bodyX = x + (width - candleWidth) / 2;
  const bodyColor = payload.isBullish ? "#79f8b5" : "#ff8f98";

  return (
    <rect
      x={bodyX}
      y={bodyY}
      width={candleWidth}
      height={bodyHeight}
      rx={2}
      fill={bodyColor}
      opacity={0.92}
    />
  );
};

const CandleWickShape = ({ x, y, width, height, payload }) => {
  if (height <= 0) {
    return null;
  }

  const centerX = x + width / 2;
  const wickColor = payload.isBullish ? "#79f8b5" : "#ff8f98";

  return (
    <line
      x1={centerX}
      x2={centerX}
      y1={y}
      y2={y + Math.max(height, 1)}
      stroke={wickColor}
      strokeWidth={1.5}
      strokeLinecap="round"
      opacity={0.92}
    />
  );
};

const getTransferIntensity = (minted, transferred) => {
  const mintedValue = Number(minted);
  const transferredValue = Number(transferred);

  if (!Number.isFinite(mintedValue) || mintedValue <= 0 || !Number.isFinite(transferredValue)) {
    return 0;
  }

  return transferredValue / mintedValue;
};

const getSegmentWidths = (minted, transferred) => {
  const mintedValue = Number(minted) || 0;
  const transferredValue = Number(transferred) || 0;
  const total = mintedValue + transferredValue;

  if (total <= 0) {
    return {
      mintedWidth: 50,
      transferredWidth: 50,
    };
  }

  return {
    mintedWidth: (mintedValue / total) * 100,
    transferredWidth: (transferredValue / total) * 100,
  };
};

const getInitialSelectedResource = (paramName, fallbackResourceAddress) => {
  if (typeof window === "undefined") {
    return fallbackResourceAddress;
  }

  const resourceAddress = new URLSearchParams(window.location.search).get(paramName);
  return getSwapExplorerAssetByAddress(resourceAddress)?.resourceAddress || fallbackResourceAddress;
};

const buildWidgetSyncFields = (widgetSyncConfig) => {
  if (!widgetSyncConfig) {
    return null;
  }

  const { inputToken, outputToken } = widgetSyncConfig;

  return {
    inputAmount: WIDGET_SYNC_AMOUNT,
    "inputToken[address]": inputToken.resourceAddress,
    "inputToken[symbol]": inputToken.symbol,
    "inputToken[name]": inputToken.name,
    "inputToken[description]": inputToken.description,
    "inputToken[iconUrl]": inputToken.iconUrl,
    "inputToken[infoUrl]": inputToken.infoUrl,
    "inputToken[divisibility]": inputToken.divisibility,
    "outputToken[address]": outputToken.resourceAddress,
    "outputToken[symbol]": outputToken.symbol,
    "outputToken[name]": outputToken.name,
    "outputToken[description]": outputToken.description,
    "outputToken[iconUrl]": outputToken.iconUrl,
    "outputToken[infoUrl]": outputToken.infoUrl,
    "outputToken[divisibility]": outputToken.divisibility,
  };
};

const Swap = () => {
  const [selectedBaseResource, setSelectedBaseResource] = useState(
    () => getInitialSelectedResource("base", DEFAULT_SWAP_BASE_RESOURCE)
  );
  const [selectedQuoteResource, setSelectedQuoteResource] = useState(
    () => getInitialSelectedResource("quote", DEFAULT_SWAP_QUOTE_RESOURCE)
  );
  const [selectedRangeId, setSelectedRangeId] = useState(CHART_RANGE_OPTIONS[1].id);
  const [chartData, setChartData] = useState({
    decimalPlaces: 5,
    noData: false,
    range: CHART_RANGE_OPTIONS[1],
    points: [],
  });
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState("");
  const widgetSyncFormRef = useRef(null);
  const widgetSyncInitializedRef = useRef(false);
  const lastWidgetSyncKeyRef = useRef(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [activitySnapshot, setActivitySnapshot] = useState(() =>
    getCachedDckslapActivitySnapshot()
  );
  const [pulseData, setPulseData] = useState(() => getCachedSwapExplorerPulse());
  const [activityLoading, setActivityLoading] = useState(() => !getCachedDckslapActivitySnapshot());
  const [pulseLoading, setPulseLoading] = useState(
    () => !getCachedSwapExplorerPulse()?.items?.length
  );
  const [activityError, setActivityError] = useState("");
  const [pulseError, setPulseError] = useState("");

  const baseAsset =
    getSwapExplorerAssetByAddress(selectedBaseResource) ||
    getSwapExplorerAssetByAddress(DEFAULT_SWAP_BASE_RESOURCE);
  const quoteOptions = getSwapExplorerQuoteCandidates(
    selectedBaseResource,
    selectedQuoteResource
  );
  const quoteAsset =
    getSwapExplorerAssetByAddress(selectedQuoteResource) ||
    quoteOptions[0] ||
    getSwapExplorerAssetByAddress(DEFAULT_SWAP_QUOTE_RESOURCE);
  const defaultBaseAsset =
    getSwapExplorerAssetByAddress(DEFAULT_SWAP_BASE_RESOURCE) || baseAsset;
  const defaultQuoteAsset =
    getSwapExplorerAssetByAddress(DEFAULT_SWAP_QUOTE_RESOURCE) || quoteAsset;
  const defaultPairLabel = `${defaultBaseAsset.symbol} / ${defaultQuoteAsset.symbol}`;
  const selectedPairLabel = `${baseAsset.symbol} / ${quoteAsset.symbol}`;
  const chartSupported = isSwapExplorerPairChartSupported(
    baseAsset.resourceAddress,
    quoteAsset.resourceAddress
  );
  const chartUrl = getSwapExplorerChartUrl(
    baseAsset.resourceAddress,
    quoteAsset.resourceAddress
  );
  const widgetSyncConfig = getSwapWidgetSyncConfig(
    baseAsset.resourceAddress,
    quoteAsset.resourceAddress
  );
  const widgetSyncFields = buildWidgetSyncFields(widgetSyncConfig);
  const widgetSyncKey = widgetSyncConfig
    ? `${widgetSyncConfig.inputToken.resourceAddress}::${widgetSyncConfig.outputToken.resourceAddress}`
    : null;
  const widgetSyncCopy = widgetSyncConfig
    ? widgetSyncConfig.mode === "exact"
      ? {
          state: "Widget synced",
          toneClass: "is-synced",
          body: `The official rail is following ${selectedPairLabel}.`,
          actionLabel: "Re-sync widget",
        }
      : {
          state: "Same token pair",
          toneClass: "is-partial",
          body: `The chart is reversed, but the official rail only trades ${widgetSyncConfig.inputToken.symbol} into ${widgetSyncConfig.outputToken.symbol}.`,
          actionLabel: "Keep widget on DCKS -> LSULP",
        }
    : {
        state: "Widget limited",
        toneClass: "is-limited",
        body: `From this frontend, the official rail only mirrors ${defaultPairLabel}. Other chart pairs stay chart-only.`,
        actionLabel: `Use ${defaultPairLabel} in both`,
      };
  const featuredPairs = getSwapExplorerFeaturedPairs();
  const activityStats = activitySnapshot?.stats || null;
  const footprintMetrics = activityStats
    ? [
        {
          label: "Players seeded",
          value: formatInteger(activityStats.totalPlayersCount),
          note: "Wallets discovered in the candy-machine user store.",
        },
        {
          label: "DCKSLAP participants",
          value: formatInteger(activityStats.totalParticipants),
          note: "Unique addresses touching DCKSLAP mint or transfer flows.",
        },
        {
          label: "Games closed",
          value: formatInteger(activityStats.gamesPlayedCount),
          note: "Successful candy-machine rounds observed on-ledger.",
        },
        {
          label: "Withdrawals tracked",
          value: formatInteger(activityStats.globalDepositCount),
          note: "Successful player withdrawal events from the DCKSLAP loop.",
        },
        {
          label: "Utility throughput",
          value: formatCompactNumber(activityStats.grandTotalVolume),
          note: "Combined mint, transfer, withdrawal, and game-round activity.",
        },
      ]
    : [];
  const economyRows = activityStats
    ? [
        {
          label: "DCKSLAP loop",
          minted: activityStats.dckslapTotalMinted,
          transferred: activityStats.dckslapTotalTransferred,
          accentClass: "is-violet",
        },
        {
          label: "GBOF reward loop",
          minted: activityStats.gbofTotalMinted,
          transferred: activityStats.gbofTotalTransferred,
          accentClass: "is-orange",
        },
      ]
    : [];
  const economySignals = activityStats
    ? [
        {
          label: "Transfer intensity",
          value: formatRatio(
            getTransferIntensity(
              activityStats.grandTotalMinted,
              activityStats.grandTotalTransferred
            )
          ),
          note: "How heavily the utility loop circulates after minting.",
        },
        {
          label: "Games per player",
          value: formatRatio(
            activityStats.totalPlayersCount > 0
              ? activityStats.gamesPlayedCount / activityStats.totalPlayersCount
              : 0
          ),
          note: "Observed candy-machine rounds divided by discovered players.",
        },
        {
          label: "Withdrawals per participant",
          value: formatRatio(
            activityStats.totalParticipants > 0
              ? activityStats.globalDepositCount / activityStats.totalParticipants
              : 0
          ),
          note: "How often active DCKSLAP participants complete a withdrawal loop.",
        },
      ]
    : [];
  const chartPoints = chartData.points || [];
  const firstChartPoint = chartPoints[0];
  const lastChartPoint = chartPoints[chartPoints.length - 1];
  const priceChange = lastChartPoint && firstChartPoint
    ? lastChartPoint.close - firstChartPoint.close
    : 0;
  const priceChangePercent = firstChartPoint?.close
    ? (priceChange / firstChartPoint.close) * 100
    : 0;
  const { chartHigh, chartLow, yAxisDomain } = getChartScale(chartPoints);
  const candlePoints = chartPoints.map((point) => {
    const bodyBase = Math.min(point.open, point.close);
    const bodyTop = Math.max(point.open, point.close);

    return {
      ...point,
      candleOffset: point.low,
      bodyHeight: bodyTop - bodyBase,
      wickLower: bodyBase - point.low,
      wickUpper: point.high - bodyTop,
      isBullish: point.close >= point.open,
    };
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("base", baseAsset.resourceAddress);
    currentUrl.searchParams.set("quote", quoteAsset.resourceAddress);
    window.history.replaceState(window.history.state, "", currentUrl.toString());
  }, [baseAsset.resourceAddress, quoteAsset.resourceAddress]);

  useEffect(() => {
    const nextWidgetSyncConfig = getSwapWidgetSyncConfig(
      baseAsset.resourceAddress,
      quoteAsset.resourceAddress
    );
    const nextWidgetSyncFields = buildWidgetSyncFields(nextWidgetSyncConfig);
    const nextWidgetSyncKey = nextWidgetSyncConfig
      ? `${nextWidgetSyncConfig.inputToken.resourceAddress}::${nextWidgetSyncConfig.outputToken.resourceAddress}`
      : null;

    if (!nextWidgetSyncKey || !nextWidgetSyncFields) {
      lastWidgetSyncKeyRef.current = null;
      return;
    }

    if (!widgetSyncInitializedRef.current) {
      widgetSyncInitializedRef.current = true;
      lastWidgetSyncKeyRef.current = nextWidgetSyncKey;
      return;
    }

    if (lastWidgetSyncKeyRef.current === nextWidgetSyncKey || !widgetSyncFormRef.current) {
      return;
    }

    // The official rail is cross-origin, so pair sync happens by POSTing the market into its iframe.
    setWidgetLoaded(false);
    lastWidgetSyncKeyRef.current = nextWidgetSyncKey;
    widgetSyncFormRef.current.submit();
  }, [baseAsset.resourceAddress, quoteAsset.resourceAddress]);

  useEffect(() => {
    const nextQuoteOptions = getSwapExplorerQuoteCandidates(
      selectedBaseResource,
      selectedQuoteResource
    );

    if (!nextQuoteOptions.length) {
      return;
    }

    const selectedQuoteIsValid = nextQuoteOptions.some(
      (asset) => asset.resourceAddress === selectedQuoteResource
    );

    if (selectedBaseResource === selectedQuoteResource || !selectedQuoteIsValid) {
      const fallbackQuote =
        nextQuoteOptions.find(
          (asset) => asset.resourceAddress !== selectedBaseResource
        ) || nextQuoteOptions[0];

      if (fallbackQuote?.resourceAddress && fallbackQuote.resourceAddress !== selectedQuoteResource) {
        setSelectedQuoteResource(fallbackQuote.resourceAddress);
      }
    }
  }, [selectedBaseResource, selectedQuoteResource]);

  useEffect(() => {
    let isCancelled = false;

    if (!chartSupported) {
      setChartLoading(false);
      setChartError("");
      setChartData((current) => ({
        ...current,
        noData: false,
        points: [],
      }));
      return () => {
        isCancelled = true;
      };
    }

    const loadChart = async () => {
      setChartLoading(true);
      setChartError("");

      try {
        const nextChartData = await fetchSwapExplorerChartSeries(
          baseAsset.resourceAddress,
          quoteAsset.resourceAddress,
          selectedRangeId
        );

        if (!isCancelled) {
          setChartData(nextChartData);
        }
      } catch (error) {
        if (!isCancelled) {
          setChartError("Chart data is temporarily unavailable.");
        }
      } finally {
        if (!isCancelled) {
          setChartLoading(false);
        }
      }
    };

    loadChart();

    return () => {
      isCancelled = true;
    };
  }, [
    baseAsset.resourceAddress,
    chartSupported,
    quoteAsset.resourceAddress,
    selectedRangeId,
  ]);

  useEffect(() => {
    let isCancelled = false;
    const cachedSnapshot = getCachedDckslapActivitySnapshot();
    const cachedPulse = getCachedSwapExplorerPulse();

    if (cachedSnapshot) {
      setActivitySnapshot(cachedSnapshot);
      setActivityLoading(false);
    }

    if (cachedPulse?.items?.length) {
      setPulseData(cachedPulse);
      setPulseLoading(false);
    }

    const loadIntel = async () => {
      setActivityLoading(!cachedSnapshot);
      setPulseLoading(!cachedPulse?.items?.length);

      const [snapshotResult, pulseResult] = await Promise.allSettled([
        fetchDckslapActivitySnapshot(),
        fetchSwapExplorerPulse(),
      ]);

      if (isCancelled) {
        return;
      }

      if (snapshotResult.status === "fulfilled") {
        setActivitySnapshot(snapshotResult.value);
        setActivityError("");
      } else if (!cachedSnapshot) {
        setActivityError("Utility footprint is temporarily unavailable.");
      }

      if (pulseResult.status === "fulfilled") {
        setPulseData(pulseResult.value);
        setPulseError("");
      } else if (!cachedPulse?.items?.length) {
        setPulseError("Recent network activity could not be loaded.");
      }

      setActivityLoading(false);
      setPulseLoading(false);
    };

    loadIntel();

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleReversePair = () => {
    setSelectedBaseResource(selectedQuoteResource);
    setSelectedQuoteResource(selectedBaseResource);
  };

  const resetToDefaultPair = () => {
    setSelectedBaseResource(DEFAULT_SWAP_BASE_RESOURCE);
    setSelectedQuoteResource(DEFAULT_SWAP_QUOTE_RESOURCE);
  };

  const syncWidgetToChartPair = () => {
    if (!widgetSyncFields || !widgetSyncFormRef.current || !widgetSyncKey) {
      return;
    }

    setWidgetLoaded(false);
    lastWidgetSyncKeyRef.current = widgetSyncKey;
    widgetSyncInitializedRef.current = true;
    widgetSyncFormRef.current.submit();
  };

  return (
    <div className="swap-page" data-scroll-root>
      <Header />
      <main className="swap-content">
        <section className="swap-workspace">
          <div className="container">
            <div className="swap-workspace__grid">
              <section className="swap-main-panel">
                <div className="swap-chart-shell">
                  <div className="swap-chart-shell__topline">
                    <span className="swap-chart-state">
                      {!chartSupported
                        ? "Registry only"
                        : chartError
                          ? "Fallback"
                          : chartLoading
                            ? "Loading chart"
                            : "Live chart"}
                    </span>
                    <span className="swap-chart-caption">{selectedPairLabel}</span>
                  </div>

                  {!chartSupported || chartError ? (
                    <div className="swap-chart-fallback">
                      <div className="swap-chart-fallback__icon">
                        <FaLayerGroup />
                      </div>
                      <h3>
                        {!chartSupported
                          ? "This pair is in the explorer registry, but not marked as chart-backed yet."
                          : "Native chart data did not come back cleanly."}
                      </h3>
                      <p>
                        {!chartSupported
                          ? "Keep the token in the registry for context, or jump back to a featured pair for a live chart."
                          : `Use the direct chart link while the feed is unavailable, or snap back to the default ${defaultPairLabel} view.`}
                      </p>
                      <div className="swap-chart-fallback__actions">
                        <a
                          href={chartSupported ? chartUrl : RADIX_CHARTS_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="swap-inline-link"
                        >
                          Open chart hub <FaExternalLinkAlt />
                        </a>
                        <button type="button" className="swap-text-button" onClick={resetToDefaultPair}>
                          Reset {defaultPairLabel}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="swap-chart-native">
                      <div className="swap-chart-native__header">
                        <div className="swap-chart-native__price">
                          <strong>
                            {lastChartPoint
                              ? formatChartValue(lastChartPoint.close, chartData.decimalPlaces)
                              : "--"}
                          </strong>
                          <span className={priceChange >= 0 ? "is-positive" : "is-negative"}>
                            {lastChartPoint
                              ? `${priceChange >= 0 ? "+" : ""}${formatChartValue(
                                  priceChange,
                                  chartData.decimalPlaces
                                )} (${priceChange >= 0 ? "+" : ""}${oneDecimalFormatter.format(
                                  priceChangePercent
                                )}%)`
                              : "Waiting for chart points"}
                          </span>
                        </div>

                        <div className="swap-chart-native__controls">
                          <div className="swap-chart-native__picker-row">
                            <label className="swap-control-field">
                              <span>Asset</span>
                              <select
                                value={baseAsset.resourceAddress}
                                onChange={(event) => setSelectedBaseResource(event.target.value)}
                              >
                                {SWAP_EXPLORER_ASSETS.map((asset) => (
                                  <option key={asset.resourceAddress} value={asset.resourceAddress}>
                                    {asset.symbol} - {asset.name}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <button
                              type="button"
                              className="swap-reverse-button"
                              onClick={handleReversePair}
                              aria-label="Reverse selected pair"
                            >
                              <FaExchangeAlt />
                            </button>

                            <label className="swap-control-field">
                              <span>Quote</span>
                              <select
                                value={quoteAsset.resourceAddress}
                                onChange={(event) => setSelectedQuoteResource(event.target.value)}
                              >
                                {quoteOptions.map((asset) => (
                                  <option key={asset.resourceAddress} value={asset.resourceAddress}>
                                    {asset.symbol} - {asset.name}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>

                          <div className="swap-chart-native__toolbar">
                            <div className="swap-pair-shortcuts" aria-label="Featured explorer pairs">
                              {featuredPairs.map(
                                ({ baseAsset: quickBaseAsset, quoteAsset: quickQuoteAsset }) => {
                                  const isActive =
                                    quickBaseAsset.resourceAddress === baseAsset.resourceAddress &&
                                    quickQuoteAsset.resourceAddress === quoteAsset.resourceAddress;

                                  return (
                                    <button
                                      key={`${quickBaseAsset.resourceAddress}::${quickQuoteAsset.resourceAddress}`}
                                      type="button"
                                      className={`swap-pair-shortcut ${
                                        isActive ? "is-active" : ""
                                      }`}
                                      onClick={() => {
                                        setSelectedBaseResource(quickBaseAsset.resourceAddress);
                                        setSelectedQuoteResource(quickQuoteAsset.resourceAddress);
                                      }}
                                    >
                                      {quickBaseAsset.symbol} / {quickQuoteAsset.symbol}
                                    </button>
                                  );
                                }
                              )}
                            </div>

                            <div className="swap-range-switcher">
                              {CHART_RANGE_OPTIONS.map((option) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  className={`swap-range-switch ${
                                    selectedRangeId === option.id ? "is-active" : ""
                                  }`}
                                  onClick={() => setSelectedRangeId(option.id)}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="swap-chart-metrics">
                        <div>
                          <span>High</span>
                          <strong>
                            {chartHigh !== null
                              ? formatChartValue(chartHigh, chartData.decimalPlaces)
                              : "--"}
                          </strong>
                        </div>
                        <div>
                          <span>Low</span>
                          <strong>
                            {chartLow !== null
                              ? formatChartValue(chartLow, chartData.decimalPlaces)
                              : "--"}
                          </strong>
                        </div>
                        <div>
                          <span>Bars</span>
                          <strong>{formatInteger(chartPoints.length)}</strong>
                        </div>
                      </div>

                      <div className="swap-chart-frame">
                        {chartLoading ? (
                          <div className="swap-chart-frame__loading">
                            <div className="swap-spinner" />
                            <span>Loading the selected chart...</span>
                          </div>
                        ) : chartData.noData || !chartPoints.length ? (
                          <div className="swap-chart-fallback is-inline">
                            <h3>No chart bars returned for this range.</h3>
                            <p>Try another range or open the pair directly on CaviarNine.</p>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={440}>
                            <BarChart
                              data={candlePoints}
                              margin={{ top: 8, right: 12, left: -18, bottom: 18 }}
                              barCategoryGap="30%"
                            >
                              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" />
                              <XAxis
                                dataKey="timestamp"
                                tickFormatter={(value) => formatChartTick(value, selectedRangeId)}
                                tick={{ fill: "rgba(232,232,238,0.6)", fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={24}
                              />
                              <YAxis
                                domain={yAxisDomain}
                                allowDataOverflow
                                tickFormatter={(value) =>
                                  formatChartValue(value, chartData.decimalPlaces)
                                }
                                tick={{ fill: "rgba(232,232,238,0.6)", fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                width={82}
                              />
                              <Tooltip
                                content={
                                  <SwapChartTooltip decimalPlaces={chartData.decimalPlaces} />
                                }
                              />
                              <Bar
                                dataKey="candleOffset"
                                stackId="candles"
                                fill="transparent"
                                stroke="transparent"
                                isAnimationActive={false}
                              />
                              <Bar
                                dataKey="wickLower"
                                stackId="candles"
                                fill="transparent"
                                stroke="transparent"
                                shape={<CandleWickShape />}
                                isAnimationActive={false}
                              />
                              <Bar
                                dataKey="bodyHeight"
                                stackId="candles"
                                fill="transparent"
                                stroke="transparent"
                                shape={<CandleBodyShape />}
                                maxBarSize={14}
                                isAnimationActive={false}
                              />
                              <Bar
                                dataKey="wickUpper"
                                stackId="candles"
                                fill="transparent"
                                stroke="transparent"
                                shape={<CandleWickShape />}
                                isAnimationActive={false}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <aside className="swap-rail">
                <form
                  ref={widgetSyncFormRef}
                  method="post"
                  action={SWAP_WIDGET_URL}
                  target={WIDGET_IFRAME_NAME}
                  className="swap-widget-sync-form"
                  aria-hidden="true"
                >
                  {(widgetSyncFields ? Object.entries(widgetSyncFields) : []).map(([name, value]) => (
                    <input key={name} type="hidden" name={name} value={value} readOnly />
                  ))}
                </form>

                <div className={`swap-widget-shell ${widgetLoaded ? "is-loaded" : ""}`}>
                  {!widgetLoaded && (
                    <div className="swap-widget-shell__loading">
                      <div className="swap-spinner" />
                      <span>Loading the official REDDICKS rail...</span>
                    </div>
                  )}

                  <iframe
                    title="Official REDDICKS swap widget"
                    name={WIDGET_IFRAME_NAME}
                    src={SWAP_WIDGET_URL}
                    className="swap-widget-shell__frame"
                    loading="eager"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="clipboard-write"
                    onLoad={() => setWidgetLoaded(true)}
                  />
                </div>

                <div className="swap-rail__meta">
                  <span>{widgetLoaded ? "Official REDDICKS rail live" : "Loading official REDDICKS rail"}</span>
                  {selectedPairLabel !== defaultPairLabel && (
                    <button type="button" className="swap-text-button" onClick={resetToDefaultPair}>
                      Reset {defaultPairLabel}
                    </button>
                  )}
                </div>

                <div className={`swap-widget-sync-card ${widgetSyncCopy.toneClass}`}>
                  <div className="swap-widget-sync-card__topline">
                    <strong>{widgetSyncCopy.state}</strong>
                    <span>{widgetSyncConfig ? "Official rail" : "Chart only"}</span>
                  </div>
                  <p>{widgetSyncCopy.body}</p>
                  <button
                    type="button"
                    className="swap-text-button"
                    onClick={widgetSyncConfig ? syncWidgetToChartPair : resetToDefaultPair}
                  >
                    {widgetSyncCopy.actionLabel}
                  </button>
                </div>

                <div className="swap-rail__links">
                  <a
                    href={SWAP_WIDGET_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="swap-inline-link"
                  >
                    Standalone widget <FaExternalLinkAlt />
                  </a>
                  <Link to="/verify" className="swap-inline-link">
                    Verify liquidity <FaArrowRight />
                  </Link>
                  <a
                    href={RADIX_BUY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="swap-inline-link"
                  >
                    Radix trade venue <FaExternalLinkAlt />
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </section>
        <section className="swap-intel">
          <div className="container">
            <div className="swap-intel__intro">
              <div>
                <span className="swap-section-kicker">Radix Utility Intel</span>
                <h2 className="swap-section-title">The part people usually miss.</h2>
              </div>
              <p>
                Price action is only the surface. These blocks show how REDDICKS utility actually behaves on
                the Radix ledger underneath the chart.
              </p>
            </div>

            <div className="swap-intel__grid">
              <article className="swap-intel-card swap-intel-card--footprint">
                <div className="swap-intel-card__header">
                  <div>
                    <span className="swap-panel-kicker">Utility Footprint</span>
                    <h3>Real usage breadth, not just swap traffic</h3>
                  </div>
                  <span className="swap-card-meta">
                    {activitySnapshot?.fetchedAt
                      ? `Updated ${formatTimeAgo(activitySnapshot.fetchedAt)}`
                      : "Waiting on network data"}
                  </span>
                </div>

                {activityLoading && !activityStats ? (
                  <div className="swap-card-empty">Loading the latest utility snapshot...</div>
                ) : activityError && !activityStats ? (
                  <div className="swap-card-empty">{activityError}</div>
                ) : (
                  <div className="swap-stat-grid">
                    {footprintMetrics.map((metric) => (
                      <article key={metric.label} className="swap-stat-card">
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                        <p>{metric.note}</p>
                      </article>
                    ))}
                  </div>
                )}
              </article>

              <article className="swap-intel-card swap-intel-card--economy">
                <div className="swap-intel-card__header">
                  <div>
                    <span className="swap-panel-kicker">Game Economy Split</span>
                    <h3>How the loop behaves after minting</h3>
                  </div>
                  <span className="swap-card-meta">Raw unit flow, not dollarized value</span>
                </div>

                {activityLoading && !activityStats ? (
                  <div className="swap-card-empty">Building the economy split...</div>
                ) : activityError && !activityStats ? (
                  <div className="swap-card-empty">{activityError}</div>
                ) : (
                  <>
                    <div className="swap-economy-rows">
                      {economyRows.map((row) => {
                        const { mintedWidth, transferredWidth } = getSegmentWidths(
                          row.minted,
                          row.transferred
                        );

                        return (
                          <article
                            key={row.label}
                            className={`swap-economy-row ${row.accentClass}`}
                          >
                            <div className="swap-economy-row__topline">
                              <strong>{row.label}</strong>
                              <span>{formatRatio(getTransferIntensity(row.minted, row.transferred))}</span>
                            </div>

                            <div className="swap-flow-bar">
                              <span style={{ width: `${mintedWidth}%` }} className="is-minted" />
                              <span
                                style={{ width: `${transferredWidth}%` }}
                                className="is-transferred"
                              />
                            </div>

                            <div className="swap-economy-row__stats">
                              <span>Minted {formatCompactNumber(row.minted)}</span>
                              <span>Transferred {formatCompactNumber(row.transferred)}</span>
                            </div>
                          </article>
                        );
                      })}
                    </div>

                    <div className="swap-signal-grid">
                      {economySignals.map((signal) => (
                        <article key={signal.label} className="swap-signal-card">
                          <span>{signal.label}</span>
                          <strong>{signal.value}</strong>
                          <p>{signal.note}</p>
                        </article>
                      ))}
                    </div>
                  </>
                )}
              </article>

              <article className="swap-intel-card swap-intel-card--pulse">
                <div className="swap-intel-card__header">
                  <div>
                    <span className="swap-panel-kicker">Recent Network Pulse</span>
                    <h3>Fresh activity from the utility loop</h3>
                  </div>
                  <span className="swap-card-meta">
                    {pulseData?.fetchedAt
                      ? `Updated ${formatTimeAgo(pulseData.fetchedAt)}`
                      : "Waiting on gateway stream"}
                  </span>
                </div>

                {pulseLoading && !pulseData?.items?.length ? (
                  <div className="swap-card-empty">Loading recent network activity...</div>
                ) : pulseError && !pulseData?.items?.length ? (
                  <div className="swap-card-empty">{pulseError}</div>
                ) : (
                  <div className="swap-pulse-list">
                    {(pulseData?.items || []).map((item) => (
                      <article key={item.id} className="swap-pulse-item">
                        <div className="swap-pulse-item__header">
                          <span className={`swap-pulse-pill is-${item.kind}`}>{item.kindLabel}</span>
                          <span>{formatTimeAgo(item.confirmedAt)}</span>
                        </div>

                        <h4>{item.title}</h4>
                        <p>{item.summary}</p>

                        <div className="swap-pulse-item__meta">
                          <span>State #{formatInteger(item.stateVersion)}</span>
                          <span>Fee {formatFee(item.feePaid)}</span>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </article>
            </div>

          </div>
        </section>
      </main>
      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Swap;

